import { useCallback, useEffect, useRef, useState } from 'react'

import { streamGenerateArticleRewrite } from '@/common/api/articleRewriteStream'
import { useCallMutation } from '@/common/api/client'
import {
  ArticleDiagnosisApiPath,
  ArticleRewriteProgressEvent,
  DiagnoseArticleResponse,
  DiagnoseArticleVars,
  GenerateArticleRewriteStreamResult,
  GenerateArticleRewriteStreamVars
} from '@/common/api/params/articleDiagnosis'

// -----------------------------------------------
// Mutation hooks
// -----------------------------------------------

export const useDiagnoseArticleMutation = (options?: {
  onSuccess?: (response: DiagnoseArticleResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DiagnoseArticleResponse, DiagnoseArticleVars>(
    ArticleDiagnosisApiPath.DIAGNOSE_ARTICLE,
    options
  )
}

// -----------------------------------------------
// SSE streaming hook（AIリライト案生成）
// -----------------------------------------------

export interface UseGenerateArticleRewriteStreamState {
  rewriting: boolean
  progress: ArticleRewriteProgressEvent | null
  result: GenerateArticleRewriteStreamResult | null
  error: string | null
  start: (vars: GenerateArticleRewriteStreamVars) => Promise<void>
  reset: () => void
}

export const useGenerateArticleRewriteStream = (): UseGenerateArticleRewriteStreamState => {
  const [rewriting, setRewriting] = useState(false)
  const [progress, setProgress] = useState<ArticleRewriteProgressEvent | null>(null)
  const [result, setResult] = useState<GenerateArticleRewriteStreamResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    setRewriting(false)
    setProgress(null)
    setResult(null)
    setError(null)
  }, [])

  const start = useCallback(async (vars: GenerateArticleRewriteStreamVars) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setRewriting(true)
    setError(null)
    setProgress({
      step: 0,
      total_steps: 5,
      percent: 0,
      message: 'リライト案の生成を開始しています...',
      detail: ''
    })

    try {
      await streamGenerateArticleRewrite(
        vars,
        {
          onProgress: (p) => mountedRef.current && setProgress(p),
          onResult: (r) => mountedRef.current && setResult(r)
        },
        controller.signal
      )
    } catch (e) {
      if (mountedRef.current && !(e instanceof DOMException && e.name === 'AbortError')) {
        setError('リライト案の生成に失敗しました')
      }
    } finally {
      if (mountedRef.current) {
        setRewriting(false)
        setProgress(null)
      }
    }
  }, [])

  return { rewriting, progress, result, error, start, reset }
}
